import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  Typography,
} from '@material-ui/core/';
import { useMutation, useQuery } from '@apollo/client';
import { useGlobal } from '@rcg/ui-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { VALIDATE_CAS } from '../../../graphql/mutations';
import { GET_COUNTRIES } from '../../../graphql/queries';
import { DatePicker, Select, TextField } from '../../../components';
import { birthDateValidation } from '../index.validation';
import useStyles from '../index.style';
import {
  capitalizeText,
  formatDate,
  FIRST_NAME_MAX_LENGTH,
  LAST_NAME_MAX_LENGTH,
} from '../../../commons';
import Loader from '../../../components/Loader/index';
import {
  checkFieldUpdated,
  getGuestInfoFlags,
  disableEditGuestInformation,
  getSessionStorageItem,
} from '../../../commons/utils';
import { ENABLE_TRAVEL_DOCUMENT_FIELD } from '../../../commons/ld-user';

export default (props) => {
  const {
    change,
    errors: { guests: guestsErrors },
    guests,
    setFieldError,
    setFieldValue,
    setValidCasResponse,
    values: { guests: guestsValues },
    currentBookingInfo,
    setFieldUpdated,
    updatedFieldsState,
    ldFlags,
  } = props;

  const classes = useStyles();
  const reservation = props.values;
  const isAFlightbooked = guests.some((guest) => guest.airAccommodations !== null);
  const { bookingType, showEditGuestInformationCta, showViewGuestInformationCta } =
    getGuestInfoFlags(currentBookingInfo, ldFlags);
  const getStatusFromSStorage = getSessionStorageItem('current-booking');
  const status = getStatusFromSStorage?.status || 'BOOKED';
  const enableTravelDocument = ldFlags?.[ENABLE_TRAVEL_DOCUMENT_FIELD];

  const disableField = disableEditGuestInformation(
    bookingType,
    showEditGuestInformationCta,
    showViewGuestInformationCta,
    isAFlightbooked,
    status,
  );

  const {
    renderContent,
    data: { localizedDayjs },
  } = useGlobal();
  const { loading: countryLoading, data: countryData } = useQuery(GET_COUNTRIES);

  const [countries, setCountries] = useState(null);
  const [countryDivisions, setCountryDivisions] = useState(null);
  const countriesList = countryData && countryData.allCountries;
  const statePlaceholder = renderContent(
    'formBookingAmendStateFieldPlaceholder',
    'State or province',
  );
  useEffect(() => {
    if (countriesList) {
      setCountries(countriesList);
    }
  }, [countriesList, countries]);

  const I18rccl = {
    ageError: renderContent(
      'formBookingAmendValidationAge',
      'That age does not fall within the age group selected during booking. If you still need to make this change, please call us for assistance',
    ),
    birthDateError: renderContent(
      'formBookingAmendValidationBirthDate',
      'That age does not fall within the age group selected during booking. If you still need to make this change, please call us for assistance',
    ),
    lockedField: renderContent('formBookingAmendLockedField', 'This field is not editable'),
    invalidCas: renderContent(
      'formBookingAmendInvalidCrownAnchor',
      'Invalid Crown and Anchor number',
    ),
  };

  const sailDate = formatDate(reservation.sailing.sailDate, localizedDayjs);

  const GuestFields = guests.map((guest, i) => {
    const isPrimary = guest.index === 1;
    const isBrasilCountry = guestsValues[i].nationality === 'BR';
    const cpfExist = guestsValues[i]?.travelDocument?.length > 3;
    const inputDate = formatDate(guestsValues[i].birthDate, localizedDayjs);

    // Handle BirthDate State
    const [guestDOB, handleDateChange] = useState({
      [i]: formatDate(guest.birthDate, localizedDayjs),
    });
    const [guestDOBInputValue, handleDateChangeInputValue] = useState({
      [i]: localizedDayjs(guest.birthDate).format('MM/DD/YYYY'),
    });

    const [cas, setValidateCas] = useState({
      [i]: { isValid: guestsValues[i].cas > 0, validating: false },
    });

    const [validateCas, { loading: casLoading }] = useMutation(VALIDATE_CAS, {
      onCompleted: (result) => {
        setValidateCas({
          [i]: { isValid: result.validateCas, validating: true },
        });
        setValidCasResponse(result.validateCas);
        if (!result.validateCas) setFieldError(`guests.${i}.cas`, I18rccl.invalidCas);
      },
    });

    const onChangeHandler = ({ target: { name, value } }) => {
      if (
        (name.includes('firstName') && value.length <= FIRST_NAME_MAX_LENGTH) ||
        (name.includes('lastName') && value.length <= LAST_NAME_MAX_LENGTH)
      ) {
        setFieldValue(name, value);
        checkFieldUpdated(currentBookingInfo, name, value, i, setFieldUpdated, updatedFieldsState);
      }
    };

    const changePhoneNumber = (phoneNumber) => {
      checkFieldUpdated(
        currentBookingInfo,
        'phoneNumber',
        phoneNumber,
        i,
        setFieldUpdated,
        updatedFieldsState,
      );
      if (phoneNumber.length > 7) {
        setFieldValue(`guests.${i}.phoneNumber`, phoneNumber);
      }
      if (phoneNumber.length <= 7) {
        setFieldUpdated(false);
      }
    };

    const onChangeCAS = (event) => {
      const { value } = event.target;
      setFieldValue(`guests.${i}.cas`, value);
      checkFieldUpdated(currentBookingInfo, 'cas', value, i, setFieldUpdated, updatedFieldsState);
      if (value.length === 9) {
        validateCas({
          variables: {
            cas: event.target.value,
            firstName: guestsValues[i].firstName,
            lastName: guestsValues[i].lastName,
            birthDate: guestsValues[i].birthDate,
          },
        });
      } else {
        setValidateCas({ [i]: { isValid: false, validating: false } });
        setValidCasResponse(event.target.value === '' || event.target.value === '0');
      }
    };

    const validateCountry = () => {
      setFieldValue(`guests.${i}.stateCode`, guestsValues[i].stateCode);
      if (countriesList && props.selectedTab === 0) {
        const fiterByDivisions = countriesList
          .filter((country) => country.divisions.length > 0)
          .map((country) => country.divisions);

        const allDivisions = [...fiterByDivisions[0], ...fiterByDivisions[1]];

        allDivisions.unshift({ code: 'none', name: statePlaceholder, _typename: 'Division' });
        allDivisions.unshift({
          code: 'OT',
          name: renderContent('formBookingAmendFieldStateOtherOption', 'Other'),
          _typename: 'Division',
        });
        setCountryDivisions(allDivisions);
      }
    };

    const loadInitialCountryState = () => {
      validateCountry();
    };

    // eslint-disable-next-line no-shadow
    const LoadInitialStatesField = (countries) => {
      useEffect(() => {
        loadInitialCountryState();
      }, [countries]);
      return null;
    };

    LoadInitialStatesField(countries);

    const isMutableField = (key) => guest._mutableFields.includes(key);

    let guestError = [];

    // Validate birthDate based on mutable fields
    birthDateValidation(guest, guestError, I18rccl, inputDate, sailDate);

    // Combine Local/Custom Errors and Default(guestErrors)
    if (Array.isArray(guestsErrors)) {
      guestError = { ...guestError, ...guestsErrors[i] };
    }

    const validateTaxIdOnChange = (event) => {
      const { value, name } = event.target;
      // delete not number caractes
      const numericValue = value.replace(/\D/g, '');

      // apply format "###.###.###-##" to numeric number
      const formattedValue = numericValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      setFieldValue(`guests.${i}.travelDocument`, formattedValue);
      checkFieldUpdated(currentBookingInfo, name, value, i, setFieldUpdated, updatedFieldsState);
    };

    return (
      <Typography
        data-testid="guest-field-container"
        component="div"
        key={guest.index}
        role="tabpanel"
        hidden={props.selectedTab !== i}
        id={`guest-tabpanel-${i}`}
        aria-labelledby={`guest-tab-${i}`}
        className="guestInfoForms"
        value={props.selectedTab}>
        <Box p={3}>
          <Grid
            container
            spacing={3}
            mt={2}
            className={disableField ? classes.hideIcons : classes.containerFields}>
            <Grid item xs={12} sm={6}>
              <TextField
                inputProps={{
                  'data-testid': `guests.${i}.firstName`,
                  className: `${classes.textFieldField}`,
                }}
                error={!!guestError.firstName && isMutableField('firstName')}
                helperText={guestError.firstName ? guestError.firstName : ''}
                disabled={!isMutableField('firstName') || cas[i].isValid || disableField}
                label={renderContent('formBookingAmendFieldFirstName', 'First Name')}
                name={`guests.${i}.firstName`}
                onChange={onChangeHandler}
                placeholder={renderContent('formBookingAmendFieldFirstName', 'First Name')}
                value={capitalizeText(guestsValues[i].firstName)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                inputProps={{
                  'data-testid': `guests.${i}.lastName`,
                  className: `${classes.textFieldField}`,
                }}
                error={!!guestError.lastName && isMutableField('lastName')}
                disabled={!isMutableField('lastName') || cas[i].isValid || disableField}
                helperText={guestError.lastName ? guestError.lastName : ''}
                id={`guests.${i}.lastName`}
                label={renderContent('formBookingAmendFieldLastName', 'Last Name')}
                name={`guests.${i}.lastName`}
                onChange={onChangeHandler}
                placeholder={renderContent('formBookingAmendFieldLastName', 'Last Name')}
                value={capitalizeText(guestsValues[i].lastName)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <Select
                  options={[
                    { code: 'M', name: renderContent('formBookingAmendGenderMale', 'Male') },
                    { code: 'F', name: renderContent('formBookingAmendGenderFemale', 'Female') },
                  ]}
                  inputProps={{ 'data-testid': `guests.${i}.gender` }}
                  disabled={!guest._mutableFields.includes('gender') || disableField}
                  disableUnderline
                  error={!!guestError.gender}
                  loading="true"
                  htmlFor="gender"
                  label={renderContent('formBookingAmendFieldGenderLabel', 'Gender')}
                  name={`guests.${i}.gender`}
                  onChange={change.bind(null, `guests.${i}.gender`)}
                  placeholder={renderContent('formBookingAmendFieldGenderLabel', 'Gender')}
                  value={guestsValues[i].gender}
                  tooltip={renderContent(
                    'formBookingAmendGuestGenderFieldTooltip',
                    "At this time, designation of the traveler's gender is required by TSA and other transportation security agencies and must match the traveler's government-issued ID",
                  )}
                />
                <FormHelperText>{guestError && guestError.gender}</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <>
                <DatePicker
                  inputProps={{
                    'data-testid': `guests.${i}.birthDate`,
                    className: `${classes.datePickerField}`,
                  }}
                  disabled={!isMutableField('birthDate') || cas[i].isValid || disableField}
                  error={!!guestError.birthDate}
                  format="MM/DD/YYYY"
                  fullWidth
                  helperText={guestError.birthDate ? guestError.birthDate : ''}
                  invalidDateMessage={renderContent(
                    'formBookingAmendErrorDOBInvalid',
                    'Invalid date format',
                  )}
                  label={renderContent('formBookingAmendFieldDOBLabel', 'Date of birth')}
                  name={`guests.${i}.birthDate`}
                  onChange={(e, date) => {
                    const newDate = formatDate(date, localizedDayjs);
                    handleDateChange({
                      [i]: newDate,
                    });
                    handleDateChangeInputValue({
                      [i]: date,
                    });
                    setFieldValue(`guests.${i}.birthDate`, newDate);
                    change.bind(null, `guests.${i}.birthDate`);
                    checkFieldUpdated(
                      currentBookingInfo,
                      'birthDate',
                      newDate,
                      i,
                      setFieldUpdated,
                      updatedFieldsState,
                    );
                  }}
                  value={guestDOB[i]}
                  inputValue={guestDOBInputValue[i]}
                  variant="inline"
                />
              </>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                {countryData && (
                  <Select
                    disabled={!guest._mutableFields.includes('nationality') || disableField}
                    error={!!guestError.nationality}
                    htmlFor="citizenship"
                    label={renderContent('formBookingAmendFieldCitizenship', 'Citizenship')}
                    loading={{ countryLoading }}
                    inputProps={{ 'data-testid': `guests.${i}.nationality` }}
                    name={`guests.${i}.nationality`}
                    onChange={(event) => {
                      const { value } = event.target;
                      validateCountry();
                      checkFieldUpdated(
                        currentBookingInfo,
                        'nationality',
                        value,
                        i,
                        setFieldUpdated,
                        updatedFieldsState,
                      );
                      setFieldValue(`guests.${i}.nationality`, value);
                    }}
                    options={countryData?.allCountries?.map((country) => {
                      return {
                        code: country.isoAlpha2Code,
                        name: country.name,
                      };
                    })}
                    placeholder={renderContent('formBookingAmendCitizenship', 'Citizenship')}
                    value={guestsValues[i].nationality || ''}
                  />
                )}

                <FormHelperText>{guestError?.nationality}</FormHelperText>
              </FormControl>
            </Grid>

            {isPrimary && (
              <Grid
                item
                xs={12}
                sm={6}
                style={{
                  display: !guestsValues[i].stateCode && disableField ? 'none' : 'block',
                }}>
                <FormControl fullWidth>
                  {countryDivisions && (
                    <Select
                      inputProps={{ 'data-testid': `guests.${i}.stateCode` }}
                      htmlFor="state"
                      disabled={disableField}
                      error={!!guestError.stateCode}
                      label={renderContent('formBookingAmendFieldState', 'State')}
                      loading={{ countryLoading }}
                      name={`guests.${i}.stateCode`}
                      onChange={change.bind(null, `guests.${i}.stateCode`)}
                      options={countryDivisions}
                      placeholder={renderContent('formBookingAmendFieldState', 'State')}
                      value={guestsValues[i].stateCode || 'none'}
                    />
                  )}
                </FormControl>
              </Grid>
            )}

            {/* render this field only for brasil nationality */}
            {isBrasilCountry && cpfExist && enableTravelDocument && (
              <Grid item xs={12} sm={6}>
                <TextField
                  disabled // since only read field
                  aria-label="travelDocument"
                  id={`guests.${i}.travelDocument`}
                  error={guestError && Boolean(guestError.travelDocument)}
                  helperText={guestError?.travelDocument}
                  label={renderContent('formBookingAmendFieldCpfLabel', 'CPF')}
                  name={`guests.${i}.travelDocument`}
                  onChange={validateTaxIdOnChange}
                  type="numeric"
                  value={guestsValues[i].travelDocument}
                  placeholder={renderContent(
                    'formBookingAmendFieldCpfPlaceholder',
                    '###.###.###-##',
                  )}
                  inputProps={{
                    'data-testid': `guests.${i}.travelDocument`,
                    className: `${classes.textFieldField}`,
                    maxLength: 14,
                  }}
                />
              </Grid>
            )}
            {isPrimary && (
              <Grid
                item
                xs={12}
                sm={6}
                style={{
                  display: !guestsValues[i].email && disableField ? 'none' : 'block',
                }}>
                <TextField
                  inputProps={{ className: `${classes.textFieldField}` }}
                  disabled={!guest._mutableFields.includes('email') || disableField}
                  error={!!guestError.email}
                  helperText={guestError.email ? guestError.email : ''}
                  label={renderContent('formBookingAmendFieldEmailLabel', 'Email Address')}
                  name={`guests.${i}.email`}
                  onChange={change.bind(null, `guests.${i}.email`)}
                  placeholder={renderContent('formBookingAmendFieldEmailLabel', 'Email Address')}
                  value={guestsValues[i].email}
                />
              </Grid>
            )}
            {isPrimary && (
              <Grid
                item
                xs={12}
                sm={6}
                style={{
                  display: !guestsValues[i].phoneNumber && disableField ? 'none' : 'flex',
                  alignItems: 'center',
                }}>
                <PhoneInput
                  country="us"
                  value={guestsValues[i].phoneNumber}
                  className={classes.phoneInputWrapper}
                  preferredCountries={['us', 'gb']}
                  onChange={changePhoneNumber}
                  disabled={!guest._mutableFields.includes('phoneNumber') || disableField}
                  specialLabel={renderContent('formBookingAmendFieldPhoneLabel', 'Phone')}
                  preserveOrder={['preferredCountries']}
                  error={!!guestError.phoneNumber}
                  isValid={(value) => {
                    if (value.length <= 7) {
                      return renderContent(
                        'formBookingAmendFieldPhonePlaceholder',
                        'Enter your phone number',
                      );
                    }
                    return true;
                  }}
                  defaultErrorMessage={renderContent(
                    'formBookingAmendFieldPhonePlaceholder',
                    'Enter your phone number',
                  )}
                  dropdownClass={classes.dropdownClass}
                  placeholder={renderContent(
                    'formBookingAmendFieldPhonePlaceholder',
                    'Enter your phone number',
                  )}
                  autoFormat
                  inputProps={{
                    'data-testid': 'phone',
                    className: `${classes.textFieldField} ${classes.phoneInput}`,
                    name: `guests.${i}.phoneNumber`,
                    required: true,
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                aria-label="cas"
                id={`guests.${i}.cas`}
                disabled={cas[i].isValid || disableField}
                error={!cas[i].isValid && cas[i].validating}
                helperText={!cas[i].isValid && cas[i].validating && I18rccl.invalidCas}
                label={renderContent('formBookingAmendFieldCrownAnchor', 'Crown & Anchor Number')}
                name={`guests.${i}.cas`}
                onChange={onChangeCAS.bind(this)}
                value={guestsValues[i].cas > 0 ? guestsValues[i].cas : ''}
                placeholder={renderContent(
                  'formBookingAmendCASPlaceholder',
                  'Enter your 9-digit CAS number here',
                )}
                InputProps={{
                  endAdornment: (
                    <InputAdornment className="CasIcon-root" position="end">
                      {casLoading && <Loader display />}
                    </InputAdornment>
                  ),
                }}
                // eslint-disable-next-line react/jsx-no-duplicate-props
                inputProps={{
                  'data-testid': `guests.${i}.cas`,
                  className: `${classes.textFieldField}`,
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Typography>
    );
  });

  return GuestFields;
};
